import {COMMENTS_DATE_FORMAT, formatTimeZoneDate} from '@hconnect/uikit'
import moment from 'moment-timezone'

import {TabTypeDto, KpiTabValuesDto} from '../types'
import {
  DefinitionLookup,
  DefinitionMaps,
  FilterOption,
  KpiValueLookup,
  RoleFilterValue,
  SlaCommentDisplayItem,
  SlaCommentRecord,
  TranslateFn
} from '../types/sla-comments.types'
import {
  buildItemKey,
  buildSearchValues,
  COMMENT_CONTEXT_MONTH_FORMAT,
  doesRoleMatchFilter,
  extractCommentValue,
  getCommentBody,
  getCommentDefinition,
  getContextMoment,
  getDefinitionId,
  getDefinitionName,
  getDefinitionTabType,
  getFallbackDefinitionLookup,
  getDisplayUserName,
  getNumericValue,
  getResolvedDefinitionId,
  getResolvedMonth,
  getResolvedTabType,
  getResolvedYear,
  getRoleLabel,
  getUnit,
  formatValueLabel,
  isCommentVisibleForKpi,
  matchesSearchTerm,
  normalizeUnit,
  toMoment
} from '../utils/sla-comments'

const buildContextLabel = (
  definitionName: string,
  contextMoment: moment.Moment | null,
  valueLabel: string | undefined,
  timezone: string,
  language: string
): string => {
  let label = definitionName

  if (contextMoment) {
    const contextDateLabel = formatTimeZoneDate(
      contextMoment.toISOString(),
      timezone,
      COMMENT_CONTEXT_MONTH_FORMAT,
      language
    )

    label = `${label}, ${contextDateLabel}`
  }

  if (valueLabel) {
    label = `${label}: ${valueLabel}`
  }

  return label
}

const getEventDateLabel = (
  authoredMoment: moment.Moment | null,
  contextMoment: moment.Moment | null,
  timezone: string,
  language: string
): string => {
  if (authoredMoment) {
    return formatTimeZoneDate(
      authoredMoment.toISOString(),
      timezone,
      COMMENTS_DATE_FORMAT,
      language
    )
  }

  if (contextMoment) {
    return formatTimeZoneDate(
      contextMoment.toISOString(),
      timezone,
      COMMENT_CONTEXT_MONTH_FORMAT,
      language
    )
  }

  return ''
}

const getEventTimestamp = (
  authoredMoment: moment.Moment | null,
  contextMoment: moment.Moment | null
): number => {
  return authoredMoment?.valueOf() ?? contextMoment?.valueOf() ?? 0
}

type CreateDisplayItemParams = {
  comment: SlaCommentRecord
  definitionMaps: DefinitionMaps
  kpiFilter: string
  roleFilter: RoleFilterValue
  searchValue: string
  selectedDefinitionName?: string
  t: TranslateFn
  timezone: string
  language: string
  unknownKpiLabel: string
  unknownUserLabel: string
}

export const createDisplayItem = ({
  comment,
  definitionMaps,
  kpiFilter,
  roleFilter,
  searchValue,
  selectedDefinitionName,
  t,
  timezone,
  language,
  unknownKpiLabel,
  unknownUserLabel
}: CreateDisplayItemParams): SlaCommentDisplayItem | null => {
  const extractedValue = extractCommentValue(comment)

  if (!extractedValue) {
    return null
  }

  const {commentValue, valueId} = extractedValue
  const definition = getCommentDefinition(comment.definition)
  const definitionId = getDefinitionId(definition)
  const valueLookup = definitionMaps.kpiValueMap.get(valueId)
  const fallbackDefinitionLookup = getFallbackDefinitionLookup(
    definitionId,
    valueLookup,
    definitionMaps
  )
  const definitionTabType = getDefinitionTabType(definition)
  const resolvedTabType = getResolvedTabType(
    definitionTabType,
    fallbackDefinitionLookup,
    valueLookup
  )

  if (!resolvedTabType || !doesRoleMatchFilter(roleFilter, resolvedTabType)) {
    return null
  }

  const resolvedDefinitionId = getResolvedDefinitionId(definitionId, valueLookup?.definitionId)
  const resolvedMonth = getResolvedMonth(commentValue?.month, valueLookup?.month)
  const resolvedYear = getResolvedYear(definition?.year, valueLookup?.year)
  const contextMoment = getContextMoment(resolvedYear, resolvedMonth, timezone)
  const definitionName = getDefinitionName(
    definition?.name,
    fallbackDefinitionLookup,
    valueLookup,
    unknownKpiLabel
  )
  const isVisibleForKpi = isCommentVisibleForKpi(
    resolvedDefinitionId,
    definitionName,
    kpiFilter,
    selectedDefinitionName
  )

  if (!isVisibleForKpi) {
    return null
  }

  const unit = getUnit(definition?.target?.unit, fallbackDefinitionLookup, valueLookup)
  const normalizedUnit = normalizeUnit(unit)
  const numericValue = getNumericValue(commentValue?.value, valueLookup?.value)
  const valueLabel = formatValueLabel(numericValue, normalizedUnit)
  const contextLabel = buildContextLabel(
    definitionName,
    contextMoment,
    valueLabel,
    timezone,
    language
  )
  const authoredMoment = toMoment(comment.timestamp, timezone)
  const eventDateLabel = getEventDateLabel(authoredMoment, contextMoment, timezone, language)
  const eventTimestamp = getEventTimestamp(authoredMoment, contextMoment)
  const roleLabel = getRoleLabel(resolvedTabType, t)
  const commentBody = getCommentBody(comment.commentText)
  const displayUserName = getDisplayUserName(comment.userName, unknownUserLabel)
  const searchValues = buildSearchValues(
    displayUserName,
    commentBody,
    definitionName,
    roleLabel,
    contextLabel,
    valueLabel
  )

  if (!matchesSearchTerm(searchValues, searchValue)) {
    return null
  }

  return {
    key: buildItemKey(valueId, comment.timestamp, commentBody),
    userName: displayUserName,
    comment: commentBody,
    contextLabel,
    roleLabel,
    tabType: resolvedTabType,
    eventDateLabel,
    eventTimestamp,
    kpiValueId: valueId,
    kpiName: definitionName,
    kpiUnit: normalizedUnit
  }
}

type BuildDisplayItemsParams = {
  comments: ReadonlyArray<SlaCommentRecord>
  definitionMaps: DefinitionMaps
  kpiFilter: string
  roleFilter: RoleFilterValue
  searchTerm: string
  t: TranslateFn
  timezone: string
  language: string
  unknownKpiLabel: string
  unknownUserLabel: string
}

export const mapCommentsToDisplayItems = ({
  comments,
  definitionMaps,
  kpiFilter,
  roleFilter,
  searchTerm,
  t,
  timezone,
  language,
  unknownKpiLabel,
  unknownUserLabel
}: BuildDisplayItemsParams): SlaCommentDisplayItem[] => {
  const searchValue = searchTerm.trim().toLowerCase()
  const selectedDefinitionNameRaw =
    kpiFilter !== 'all' ? definitionMaps.definitionMap.get(kpiFilter)?.definition.name : undefined
  const selectedDefinitionName =
    typeof selectedDefinitionNameRaw === 'string' ? selectedDefinitionNameRaw : undefined
  const items: SlaCommentDisplayItem[] = []

  comments.forEach((comment) => {
    const item = createDisplayItem({
      comment,
      definitionMaps,
      kpiFilter,
      roleFilter,
      searchValue,
      selectedDefinitionName,
      t,
      timezone,
      language,
      unknownKpiLabel,
      unknownUserLabel
    })

    if (item) {
      items.push(item)
    }
  })

  return items.sort((first, second) => second.eventTimestamp - first.eventTimestamp)
}

export const mapTabValuesToDefinitionMaps = (tabValues: KpiTabValuesDto[]): DefinitionMaps => {
  const definitionMap = new Map<string, DefinitionLookup>()
  const kpiValueMap = new Map<string, KpiValueLookup>()

  tabValues.forEach((tab) => {
    if (!tab.tabType) {
      return
    }

    tab.kpiDefinitions?.forEach((definition) => {
      definitionMap.set(definition.id, {definition, tabType: tab.tabType})

      definition.kpiValues?.forEach((kpiValue) => {
        kpiValueMap.set(kpiValue.id, {
          value: kpiValue.value,
          definitionId: definition.id,
          definitionName: definition.name,
          tabType: tab.tabType,
          year: definition.year,
          unit: definition.slaTarget.unit,
          month: kpiValue.month
        })
      })
    })
  })

  return {definitionMap, kpiValueMap}
}

export const mapTabValuesToRoleOptions = (
  tabValues: KpiTabValuesDto[],
  t: TranslateFn
): FilterOption<RoleFilterValue>[] => {
  const uniqueRoles = new Set<TabTypeDto>()

  tabValues.forEach((tab) => {
    if (tab.tabType) {
      uniqueRoles.add(tab.tabType)
    }
  })

  const options = Array.from(uniqueRoles)
    .map((tabType) => ({value: tabType, label: t(`tabs.${tabType}`)}))
    .sort((first, second) => first.label.localeCompare(second.label))

  return [{value: 'all' as RoleFilterValue, label: t('commentsModal.roleAll')}, ...options]
}

export const mapTabValuesToKpiOptions = (
  tabValues: KpiTabValuesDto[],
  roleFilter: RoleFilterValue,
  t: TranslateFn
): FilterOption[] => {
  const definitionMap = new Map<string, {value: string; label: string}>()

  tabValues
    .filter((tab) => roleFilter === 'all' || tab.tabType === roleFilter)
    .forEach((tab) => {
      tab.kpiDefinitions?.forEach((definition) => {
        definitionMap.set(definition.id, {value: definition.id, label: definition.name})
      })
    })

  const options = Array.from(definitionMap.values()).sort((first, second) =>
    first.label.localeCompare(second.label)
  )

  return [{value: 'all', label: t('commentsModal.kpiAll')}, ...options]
}
